import { useEffect, useRef } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { EmptyState } from '@/components/empty-state';
import { billingApi } from '@/lib/api/billing';
import { messageForError } from '@/lib/api-client';
import { getAuthState } from '@/lib/auth-store';
import { withBasePath } from '@/lib/base-path';

/**
 * Stand-in for the provider's hosted checkout page. Only the fake billing
 * provider points here; Stripe sends customers to Stripe. It completes the
 * simulated session, which emits the same webhook events a real payment would,
 * then returns to the path the checkout started from.
 */
function SimulatedCheckoutPage() {
  const { session, success } = Route.useSearch();
  const started = useRef(false);

  const complete = useMutation({
    mutationFn: () => billingApi.completeFakeCheckout(session),
    onSuccess: () => window.location.assign(withBasePath(success)),
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    complete.mutate();
  }, [complete]);

  return (
    <main className="mx-auto flex min-h-dvh max-w-lg items-center px-4">
      <EmptyState
        kicker="Simulated checkout"
        title={complete.isError ? 'Checkout could not be completed' : 'Completing your payment…'}
        description={
          complete.isError ? messageForError(complete.error) : 'This is the test payment provider. No card is charged.'
        }
      />
    </main>
  );
}

export const Route = createFileRoute('/billing/checkout/simulate')({
  validateSearch: z.object({
    session: z.string().min(1),
    // Only same-app paths are honoured, so a crafted link cannot bounce a user off-site.
    success: z
      .string()
      .refine((value) => value.startsWith('/app/') && !value.startsWith('//'))
      .catch('/app'),
  }),
  beforeLoad: ({ location }) => {
    if (getAuthState().status !== 'authenticated') {
      throw redirect({ to: '/login', search: { redirect: `${location.pathname}${location.searchStr}` } });
    }
  },
  component: SimulatedCheckoutPage,
});
